import { intranetChatDegrees, intranetChatMapUrl } from '@gb-ticket/shared-types';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useVoiceCopy } from '@/platform/voice/use-voice';

const GERMANY: { latitude: number; longitude: number } = { latitude: 51.1657, longitude: 10.4515 };

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim() ?? '';

export function canPlacePinOnMap(): boolean {
  return GOOGLE_MAPS_API_KEY.length > 0;
}

type MapsClickEvent = {
  latLng?: { lat: () => number; lng: () => number } | null;
};

type GoogleMapHandle = {
  addListener: (event: 'click', handler: (event: MapsClickEvent) => void) => void;
};

type GoogleMarkerHandle = {
  setPosition: (position: { lat: number; lng: number }) => void;
};

type GoogleMapsNamespace = {
  Map: new (
    element: HTMLElement,
    options: {
      center: { lat: number; lng: number };
      zoom: number;
      mapTypeControl: boolean;
      streetViewControl: boolean;
      fullscreenControl: boolean;
    },
  ) => GoogleMapHandle;
  Marker: new (options: {
    map: GoogleMapHandle;
    position?: { lat: number; lng: number };
  }) => GoogleMarkerHandle;
};

declare global {
  interface Window {
    google?: { maps: GoogleMapsNamespace };
  }
}

let mapsLoader: Promise<void> | null = null;

function loadGoogleMaps(key: string): Promise<void> {
  if (window.google?.maps) {
    return Promise.resolve();
  }
  mapsLoader ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&language=de&region=DE`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      mapsLoader = null;
      reject(new Error('Google Maps'));
    };
    document.head.appendChild(script);
  });
  return mapsLoader;
}

function googleEmbedUrl(latitude: number, longitude: number, zoom: number): string {
  const query = `${latitude},${longitude}`;
  return `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=${zoom}&hl=de&output=embed`;
}

/** Google Maps in the page. No Leaflet, no tile key. */
export function GoogleMapFrame({
  latitude,
  longitude,
  zoom = 16,
  className,
}: {
  latitude: number;
  longitude: number;
  zoom?: number;
  className?: string;
}) {
  return (
    <iframe
      title="Google Maps"
      src={googleEmbedUrl(latitude, longitude, zoom)}
      className={cn('h-48 w-full rounded-md border-0', className)}
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
    />
  );
}

/** Tap to place a pin when a Maps JavaScript key is configured. Otherwise the embed. */
export function GoogleMapPicker({
  latitude,
  longitude,
  onPick,
}: {
  latitude: number | null;
  longitude: number | null;
  onPick: (latitude: number, longitude: number) => void;
}) {
  const copy = useVoiceCopy();
  const hostRef = useRef<HTMLDivElement>(null);
  const onPickRef = useRef(onPick);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    onPickRef.current = onPick;
  });

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY || !hostRef.current) {
      return;
    }
    let cancelled = false;
    void loadGoogleMaps(GOOGLE_MAPS_API_KEY)
      .then(() => {
        const maps = window.google?.maps;
        const host = hostRef.current;
        if (cancelled || !maps || !host) {
          return;
        }
        const hasPin = latitude != null && longitude != null;
        const center = hasPin
          ? { lat: latitude, lng: longitude }
          : { lat: GERMANY.latitude, lng: GERMANY.longitude };
        const map = new maps.Map(host, {
          center,
          zoom: hasPin ? 16 : 6,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });
        const marker = new maps.Marker({
          map,
          position: hasPin ? center : undefined,
        });
        map.addListener('click', (event) => {
          const lat = event.latLng?.lat();
          const lng = event.latLng?.lng();
          if (lat == null || lng == null) {
            return;
          }
          marker.setPosition({ lat, lng });
          onPickRef.current(lat, lng);
        });
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
    // Create the map once. Later clicks move the marker.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial center only
  }, []);

  if (!GOOGLE_MAPS_API_KEY || failed) {
    const point = latitude != null && longitude != null ? { latitude, longitude } : GERMANY;
    return (
      <div className="space-y-2">
        <GoogleMapFrame
          latitude={point.latitude}
          longitude={point.longitude}
          zoom={latitude != null ? 16 : 6}
        />
        <p className="text-sm text-muted-foreground">{copy('intranet.location.pin-with-button')}</p>
      </div>
    );
  }

  return <div ref={hostRef} className="h-48 w-full rounded-md" />;
}

/** Bubble preview. A tap opens Google Maps larger. The frame does not cover the page menus. */
export function ChatLocationBubble({
  latitudeE7,
  longitudeE7,
}: {
  latitudeE7: number;
  longitudeE7: number;
}) {
  const [open, setOpen] = useState(false);
  const latitude = intranetChatDegrees(latitudeE7);
  const longitude = intranetChatDegrees(longitudeE7);
  const href = intranetChatMapUrl(latitudeE7, longitudeE7);
  return (
    <div className="space-y-1">
      <div className="relative overflow-hidden rounded-md">
        <GoogleMapFrame
          latitude={latitude}
          longitude={longitude}
          className="pointer-events-none h-32"
        />
        <button
          type="button"
          className="absolute inset-0"
          aria-label="Karte vergrößern"
          onClick={() => setOpen(true)}
        />
      </div>
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-xs underline">
        In Karten öffnen
      </a>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Standort</DialogTitle>
          </DialogHeader>
          <GoogleMapFrame latitude={latitude} longitude={longitude} className="h-64" />
          <Button asChild variant="outline">
            <a href={href} target="_blank" rel="noopener noreferrer">
              In Karten öffnen
            </a>
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
